import React, { useEffect, useState, useRef } from 'react';
import {
	Box,
	SimpleGrid,
	Text,
	Button,
	Spinner,
	Center,
	Container,
	Flex,
	Image,
	IconButton,
	Badge,
	Input,
	InputGroup,
	InputLeftElement,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../../component/productCard';
import logoImage from '../../assets/logo.png';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const MotionBox = motion(Box);

const Catalog = () => {
	const navigate = useNavigate();
	const [products, setProducts] = useState<any[]>([]);
	const [categories, setCategories] = useState<any[]>([]);
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [catLoading, setCatLoading] = useState(true);
	const [selectedCategory, setSelectedCategory] = useState<any>(null);
	const [selectedSubcategory, setSelectedSubcategory] = useState<any>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState<any[] | null>(null);
	const [searching, setSearching] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const parentCategories = categories.filter((c: any) => !c.parent_id);
	const subcategories = selectedCategory
		? categories.filter((c: any) => c.parent_id === selectedCategory.category_id)
		: [];

	const fetchProducts = async (off: number, catId: any, subCatId: any, append = false) => {
		try {
			const params: any = { offset: off };
			if (catId) params.categoryID = catId;
			if (subCatId) params.subcategoryID = subCatId;
			const res = await axios.get(`${BASE_URL}/api/catalog/products`, { params });
			const data = res.data?.prodData || [];
			setProducts((prev) => append ? [...prev, ...data] : data);
		} catch {
			if (!append) setProducts([]);
		}
	};

	useEffect(() => {
		const load = async () => {
			setCatLoading(true);
			try {
				const res = await axios.get(`${BASE_URL}/api/catalog/categories`);
				setCategories(Array.isArray(res.data) ? res.data : []);
			} catch { setCategories([]); }
			setCatLoading(false);
		};
		load();
	}, []);

	useEffect(() => {
		if (searchResults !== null) return;
		const load = async () => {
			setLoading(true);
			await fetchProducts(offset, selectedCategory?.category_id, selectedSubcategory?.category_id);
			setLoading(false);
		};
		load();
	}, [offset, selectedCategory, selectedSubcategory]);

	const handleSearch = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!searchQuery.trim()) { setSearchResults(null); return; }
		setSearching(true);
		try {
			const res = await axios.post(`${BASE_URL}/api/catalog/search`, { searchTerm: searchQuery.trim() });
			setSearchResults(Array.isArray(res.data) ? res.data : []);
		} catch { setSearchResults([]); }
		setSearching(false);
	};

	const clearSearch = () => {
		setSearchQuery('');
		setSearchResults(null);
	};

	const handleSelectParent = (category: any) => {
		setOffset(0);
		setSelectedCategory(category);
		setSelectedSubcategory(null);
		clearSearch();
	};

	const handleSelectSubcategory = (sub: any) => {
		setOffset(0);
		setSelectedSubcategory(sub);
		clearSearch();
	};

	const handleClearFilter = () => {
		setOffset(0);
		setSelectedCategory(null);
		setSelectedSubcategory(null);
		clearSearch();
	};

	const handleShowMore = async () => {
		setLoadingMore(true);
		await fetchProducts(offset + 20, selectedCategory?.category_id, selectedSubcategory?.category_id, true);
		setOffset((prev) => prev + 20);
		setLoadingMore(false);
	};

	const scroll = (direction: 'left' | 'right') => {
		if (containerRef.current) {
			containerRef.current.scrollBy({ left: direction === 'left' ? -320 : 320, behavior: 'smooth' });
		}
	};

	const activeName = selectedSubcategory?.category_name || selectedCategory?.category_name;
	const displayProducts = searchResults !== null ? searchResults : products;

	return (
		<>
			{/* Navbar */}
			<Flex
				as="nav"
				align="center"
				justify="space-between"
				px={{ base: 4, md: 6 }}
				py={3}
				bg="linear-gradient(135deg, #0f3460 0%, #16213e 100%)"
				boxShadow="0 2px 20px rgba(0,0,0,0.3)"
				position="sticky"
				top="0"
				zIndex="999"
				gap={3}
			>
				<Box
					flexShrink={0}
					cursor="pointer"
					onClick={handleClearFilter}
					bg="white"
					borderRadius="lg"
					px={3}
					py={1.5}
					transition="all 0.2s"
					_hover={{ opacity: 0.9 }}
				>
					<Image src={logoImage} alt="Logo" h={{ base: '24px', md: '36px' }} />
				</Box>

				<form onSubmit={handleSearch} style={{ flex: 1, maxWidth: '440px' }}>
					<InputGroup>
						<InputLeftElement pointerEvents="none">
							<SearchIcon color="gray.400" boxSize={3.5} />
						</InputLeftElement>
						<Input
							placeholder="Search products..."
							size="md"
							borderRadius="full"
							bg="whiteAlpha.100"
							color="white"
							border="1px solid"
							borderColor="whiteAlpha.200"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							_placeholder={{ color: 'whiteAlpha.500' }}
							_focus={{ bg: 'white', color: 'gray.800', borderColor: 'green.300', _placeholder: { color: 'gray.400' } }}
						/>
					</InputGroup>
				</form>

				<Flex align="center" gap={2} flexShrink={0}>
					<Button
						colorScheme="green"
						size="sm"
						borderRadius="full"
						px={5}
						fontWeight="semibold"
						onClick={() => navigate('/contact')}
						bg="green.400"
						color="white"
						_hover={{ bg: 'green.300' }}
					>
						Contact to Order
					</Button>
				</Flex>
			</Flex>

			{/* Hero announcement bar */}
			<Box
				bg="linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
				borderBottom="1px solid"
				borderColor="green.200"
				py={{ base: 5, md: 8 }}
				px={4}
			>
				<Container maxW="container.xl">
					<Flex align="center" justify="space-between" gap={4} direction={{ base: 'column', md: 'row' }} textAlign={{ base: 'center', md: 'left' }}>
						<Box>
							<Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="gray.800" mb={1}>
								Browse Our Product Catalog
							</Text>
							<Text fontSize="sm" color="gray.600" maxW="480px">
								Prices shown are base rates. Contact us for facility-specific pricing and to place an order.
							</Text>
						</Box>
						<Button
							colorScheme="green"
							size="md"
							borderRadius="full"
							px={7}
							fontWeight="bold"
							bg="green.500"
							color="white"
							_hover={{ bg: 'green.600', transform: 'translateY(-1px)', boxShadow: 'lg' }}
							transition="all 0.2s"
							flexShrink={0}
							onClick={() => navigate('/contact')}
						>
							Contact to Order
						</Button>
					</Flex>
				</Container>
			</Box>

			{/* Category Bar */}
			<AnimatePresence>
				{selectedCategory && !searchResults && (
					<MotionBox
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.25 }}
						overflow="hidden"
						bg="white"
						borderBottom="1px solid"
						borderColor="gray.200"
					>
						<Container maxW="container.xl">
							<Flex align="center" gap={4} py={3}>
								{(selectedSubcategory?.image || selectedCategory?.image) && (
									<Image
										src={`${BASE_URL}/api/image?filename=${selectedSubcategory?.image || selectedCategory?.image}`}
										alt={activeName}
										boxSize="48px"
										objectFit="contain"
										borderRadius="lg"
										flexShrink={0}
									/>
								)}
								<Box borderLeft="3px solid" borderColor="green.400" pl={3}>
									<Text fontWeight="bold" fontSize="md" color="gray.800" lineHeight="short">
										{activeName}
									</Text>
									{(selectedSubcategory?.description || selectedCategory?.description) && (
										<Text fontSize="xs" color="gray.500" noOfLines={1}>
											{selectedSubcategory?.description || selectedCategory?.description}
										</Text>
									)}
								</Box>
							</Flex>
						</Container>
					</MotionBox>
				)}
			</AnimatePresence>

			<Container maxW="container.xl" py={4}>

				{/* Category Carousel */}
				{!searchResults && (
					<Box position="relative" mb={2}>
						<IconButton
							icon={<ChevronLeftIcon fontSize="2xl" />}
							position="absolute" left={-4} top="50%" transform="translateY(-50%)"
							onClick={() => scroll('left')} bg="white" boxShadow="md"
							borderRadius="full" size="sm" zIndex={10} aria-label="Scroll left"
							_hover={{ bg: 'gray.100' }}
						/>
						<Flex overflowX="auto" px={6} py={3} gap={3} align="center" ref={containerRef}
							css={{ '&::-webkit-scrollbar': { display: 'none' }, '-ms-overflow-style': 'none', 'scrollbar-width': 'none' }}>

							<Box flexShrink={0} px={4} py={2} borderRadius="full" border="2px solid"
								borderColor={!selectedCategory ? 'green.500' : 'gray.200'}
								bg={!selectedCategory ? 'green.500' : 'white'}
								color={!selectedCategory ? 'white' : 'gray.600'}
								fontWeight="semibold" fontSize="sm" cursor="pointer"
								transition="all 0.2s"
								_hover={{ borderColor: 'green.400', bg: !selectedCategory ? 'green.500' : 'green.50' }}
								onClick={handleClearFilter} whiteSpace="nowrap">
								All Products
							</Box>

							{!catLoading && parentCategories.map((category: any) => {
								const isActive = selectedCategory?.category_id === category.category_id;
								return (
									<Box key={category.category_id} flexShrink={0} display="flex" flexDirection="column"
										alignItems="center" gap={1.5} px={3} py={2} borderRadius="xl" border="2px solid"
										borderColor={isActive ? 'green.500' : 'gray.100'} bg={isActive ? 'green.50' : 'white'}
										cursor="pointer" transition="all 0.2s" _hover={{ borderColor: 'green.300', bg: 'green.50' }}
										onClick={() => handleSelectParent(category)} minW="90px" textAlign="center">
										<Box w="48px" h="48px" borderRadius="full" bg={isActive ? 'green.100' : 'gray.50'}
											display="flex" alignItems="center" justifyContent="center" overflow="hidden">
											{category.image ? (
												<Image src={`${BASE_URL}/api/image?filename=${category.image}`}
													alt={category.category_name} boxSize="36px" objectFit="contain" />
											) : (
												<Box w="36px" h="36px" bg="gray.200" borderRadius="full" />
											)}
										</Box>
										<Text fontWeight={isActive ? 'bold' : 'medium'} fontSize="xs"
											color={isActive ? 'green.700' : 'gray.700'} noOfLines={2} lineHeight="tight">
											{category.category_name}
										</Text>
									</Box>
								);
							})}
						</Flex>
						<IconButton
							icon={<ChevronRightIcon fontSize="2xl" />}
							position="absolute" right={-4} top="50%" transform="translateY(-50%)"
							onClick={() => scroll('right')} bg="white" boxShadow="md"
							borderRadius="full" size="sm" zIndex={10} aria-label="Scroll right"
							_hover={{ bg: 'gray.100' }}
						/>
					</Box>
				)}

				{/* Subcategory pills */}
				<AnimatePresence>
					{subcategories.length > 0 && !searchResults && (
						<MotionBox initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} overflow="hidden" mb={2}>
							<Flex gap={2} px={2} py={2} flexWrap="wrap">
								<Button size="xs" borderRadius="full" variant={!selectedSubcategory ? 'solid' : 'outline'}
									colorScheme="green" onClick={() => { setSelectedSubcategory(null); setOffset(0); }}>
									All
								</Button>
								{subcategories.map((sub: any) => (
									<Button key={sub.category_id} size="xs" borderRadius="full"
										variant={selectedSubcategory?.category_id === sub.category_id ? 'solid' : 'outline'}
										colorScheme="green" onClick={() => handleSelectSubcategory(sub)}>
										{sub.category_name}
									</Button>
								))}
							</Flex>
						</MotionBox>
					)}
				</AnimatePresence>

				{/* Results header */}
				<Flex align="center" justify="space-between" mb={4} px={1}>
					<Flex align="center" gap={2}>
						<Text fontSize="sm" color="gray.500">
							{loading || searching ? 'Loading…' : `${displayProducts.length} product${displayProducts.length !== 1 ? 's' : ''}`}
						</Text>
						{searchResults !== null && (
							<Badge colorScheme="green" borderRadius="full" px={2} fontSize="xs">
								Search: "{searchQuery}"
							</Badge>
						)}
						{selectedCategory && !searchResults && (
							<Badge colorScheme="green" borderRadius="full" px={2} fontSize="xs">{activeName}</Badge>
						)}
					</Flex>
					{(selectedCategory || searchResults !== null) && (
						<Button size="xs" variant="ghost" colorScheme="gray" onClick={handleClearFilter}>
							Clear ×
						</Button>
					)}
				</Flex>

				{/* Product Grid */}
				{(loading && displayProducts.length === 0) || searching ? (
					<Center h="50vh">
						<Spinner size="xl" color="green.500" thickness="3px" />
					</Center>
				) : displayProducts.length > 0 ? (
					<>
						<MotionBox initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
							<SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={5}>
								{displayProducts.map((product: any) => (
									<ProductCard
										key={product.product_id}
										image={product.image}
										name={product.name}
										description={product.description}
										price={product.final_price}
										product_id={product.product_id}
										unit={product.unit}
										quantityPerUnit={product.quantity_per_unit}
										catalogMode={true}
										has_variants={product.has_variants}
									/>
								))}
							</SimpleGrid>
						</MotionBox>

						{searchResults === null && (
							<Center mt={10} mb={6}>
								<Button size="md" colorScheme="green" variant="outline" onClick={handleShowMore}
									isLoading={loadingMore} loadingText="Loading more…" borderRadius="full" px={8}>
									Load More Products
								</Button>
							</Center>
						)}
					</>
				) : (
					<Center h="40vh" flexDirection="column" gap={3}>
						<Text fontSize="xl" fontWeight="bold" color="gray.400">No products found</Text>
						<Button size="sm" colorScheme="green" variant="outline" onClick={handleClearFilter}>
							Show all products
						</Button>
					</Center>
				)}
			</Container>

			{/* Footer */}
			<Box as="footer" bg="gray.800" color="white" py={6} mt={10} textAlign="center">
				<Text fontSize="sm" color="gray.400">
					This is a catalog view.{' '}
					<Button variant="link" color="green.300" size="sm" onClick={() => navigate('/contact')}>
						Contact Us
					</Button>{' '}
					to place orders.
				</Text>
			</Box>
		</>
	);
};

export default Catalog;
